import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, HomeIcon, MailCheckIcon, GlobeIcon, PhoneIcon, MailIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/ewJpinVHQ8s4RJSdCh5B3M/HAA_Sep_15.png';

// Set this to the Givebutter campaign embed URL once available, e.g.
// 'https://givebutter.com/embed/c/<campaign-slug>'
const RSVP_EMBED_URL = 'https://givebutter.com/embed/c/celebracion-de-independencia-hondurena-washington-dcdmv-lumdg8';
const CONTACT_PHONE = '703-313-1465';
const CONTACT_EMAIL = 'info@honduranamerican.org';
const NAVY = '#0b2148';
const RED = '#96222a';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, HomeIcon];
const COPY = {
  EN: {
    back: 'Back to site',
    eyebrow: 'Hosted by the Honduran-American Association',
    script: 'Honduran',
    title: 'Independence Day',
    subtitle: 'Commemorating 205 years of Honduran independence, and honoring the heritage, contributions, and enduring spirit of the Honduran-American community.',
    details: [{
      label: 'Date',
      value: 'Tuesday, September 15'
    }, {
      label: 'Time',
      value: '6:00 – 8:30 PM'
    }, {
      label: 'Location',
      value: 'At the home of the President of the Honduran-American Association'
    }],
    flyerAria: 'View the full invitation graphic',
    flyerAlt: 'Honduran Independence Day — Tuesday, September 15, 6:00 to 8:30 PM, at the home of the President of the Honduran-American Association. RSVP required.',
    rsvpBadge: 'RSVP Required',
    rsvpTitle: 'Reserve Your Place',
    rsvpText: 'Attendance is by reservation only and capacity is limited. Kindly confirm your attendance below; the venue address will be provided to confirmed guests in advance of the celebration.',
    rsvpFrameTitle: 'Honduran Independence Day — RSVP',
    rsvpPending: 'The reservation form will be available here shortly. In the meantime, we invite you to confirm your attendance by telephone or email.',
    contactLead: 'For inquiries, please contact',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'An Evening of Heritage and Community',
    aboutParagraphs: ['Each September 15, Hondurans throughout the world commemorate the anniversary of our nation’s independence. This year, the Honduran-American Association is honored to host an intimate reception at the residence of its President in observance of 205 years of Honduran sovereignty.', 'Guests will join leaders, professionals, families, and friends of the Honduran-American community for an evening of meaningful conversation, cultural tradition, and shared appreciation for our common heritage.', 'The evening is both a celebration of that heritage and an invitation to strengthen the relationships that continue to advance our community across the United States.'],
    highlights: [{
      title: 'A National Commemoration',
      text: 'Two centuries of independence observed through the traditions, music, and cuisine that connect our community to Honduras.'
    }, {
      title: 'An Intimate Reception',
      text: 'A gathering at the residence of the Association’s President, designed for genuine conversation and personal connection.'
    }, {
      title: 'Community Convened',
      text: 'Leaders, families, and friends of the Honduran-American community brought together for a single evening of fellowship.'
    }],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText: 'The mission of the Honduran-American Association is to Connect, Empower, and Celebrate. Independence Day represents that mission in its fullest expression — an occasion to honor our shared history while recognizing the community shaping its future.',
    missionClosing: '¡Viva Honduras!',
    footer: 'Hosted by the Honduran-American Association'
  },
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Organizado por la Asociación Hondureño-Americana',
    script: 'Día de la',
    title: 'Independencia de Honduras',
    subtitle: 'Conmemorando 205 años de independencia hondureña, y honrando la herencia, las contribuciones y el espíritu perdurable de la comunidad hondureño-americana.',
    details: [{
      label: 'Fecha',
      value: 'Martes, 15 de septiembre'
    }, {
      label: 'Hora',
      value: '6:00 – 8:30 P.M.'
    }, {
      label: 'Lugar',
      value: 'En la casa del Presidente de la Asociación Hondureño-Americana'
    }],
    flyerAria: 'Ver la invitación completa',
    flyerAlt: 'Día de la Independencia de Honduras — Martes 15 de septiembre, 6:00 a 8:30 P.M., en la casa del Presidente de la Asociación Hondureño-Americana. Se requiere confirmación.',
    rsvpBadge: 'Confirmación Requerida',
    rsvpTitle: 'Reserve Su Lugar',
    rsvpText: 'La asistencia es únicamente por reservación y el cupo es limitado. Le agradecemos confirmar su asistencia a continuación; la dirección será proporcionada a los invitados confirmados antes de la celebración.',
    rsvpFrameTitle: 'Día de la Independencia de Honduras — Confirmación',
    rsvpPending: 'El formulario de reservación estará disponible aquí en breve. Mientras tanto, le invitamos a confirmar su asistencia por teléfono o correo electrónico.',
    contactLead: 'Para consultas, favor contactar',
    aboutEyebrow: 'Sobre la Noche',
    aboutHeading: 'Una Noche de Herencia y Comunidad',
    aboutParagraphs: ['Cada 15 de septiembre, los hondureños alrededor del mundo conmemoran el aniversario de la independencia de nuestra nación. Este año, la Asociación Hondureño-Americana tiene el honor de ofrecer una recepción íntima en la residencia de su Presidente en observancia de 205 años de soberanía hondureña.', 'Los invitados se reunirán junto a líderes, profesionales, familias y amigos de la comunidad hondureño-americana para una velada de conversación significativa, tradición cultural y aprecio compartido por nuestra herencia común.', 'La velada es a la vez una celebración de esa herencia y una invitación a fortalecer las relaciones que continúan impulsando a nuestra comunidad en los Estados Unidos.'],
    highlights: [{
      title: 'Una Conmemoración Nacional',
      text: 'Dos siglos de independencia observados a través de las tradiciones, la música y la gastronomía que conectan a nuestra comunidad con Honduras.'
    }, {
      title: 'Una Recepción Íntima',
      text: 'Un encuentro en la residencia del Presidente de la Asociación, concebido para la conversación genuina y la conexión personal.'
    }, {
      title: 'Comunidad Convocada',
      text: 'Líderes, familias y amigos de la comunidad hondureño-americana reunidos para una velada de confraternidad.'
    }],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText: 'La misión de la Asociación Hondureño-Americana es Conectar, Empoderar y Celebrar. El Día de la Independencia representa esa misión en su máxima expresión: una ocasión para honrar nuestra historia compartida y reconocer a la comunidad que construye su futuro.',
    missionClosing: '¡Viva Honduras!',
    footer: 'Organizado por la Asociación Hondureño-Americana'
  }
};
export function HonduranIndependenceDayPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? COPY.ES : COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-[#e6e1d5] font-nunito" data-id="element-3589" data-mp="2fwf3vzv4n7qy">
      {/* Flag stripe */}
      <div className="w-full" data-id="element-3590" data-mp="2kt5fa9vzlf61">
        <div className="h-1.5" style={{
        backgroundColor: NAVY
      }} data-id="element-3591" data-mp="2ks466ayj72qy" />
        <div className="h-1 bg-white" data-id="element-3592" data-mp="li4bjhfbpvgf" />
        <div className="h-1.5" style={{
        backgroundColor: RED
      }} data-id="element-3593" data-mp="ljfhnnw8rfap" />
      </div>

      <header className="w-full px-6 py-5 sm:px-12 flex items-center justify-between gap-4 bg-white border-b border-[#12336b]/10" data-id="element-3594" data-mp="qgpc5hyhmh5e">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-3595" data-mp="2nempqp5fkrhe">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3596" data-mp="322hl6datp9y6" />
        </a>
        <div className="flex items-center gap-3" data-id="element-3597" data-mp="24gk8iwj4jyu5">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-bold transition-colors hover:opacity-90" style={{
          backgroundColor: RED
        }} aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-3598" data-mp="24hv6gfpw2cdc">
            <GlobeIcon className="w-4 h-4" data-id="element-3599" data-mp="3ptws6jdjoffr" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors hover:bg-[#12336b]/5" style={{
          borderColor: `${NAVY}33`,
          color: NAVY
        }} data-id="element-3600" data-mp="1671p8mkls8p">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-3601" data-mp="3sdjl7uaxehrs" />
            <span className="hidden sm:inline" data-id="element-3602" data-mp="3seb6ab6wcx3t">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-3603" data-mp="jz1658mki1yo">
        {/* Title */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-3604" data-mp="k02kpn2sl6l5">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.32, 1]
        }} className="text-center max-w-3xl mx-auto" data-id="element-3605" data-mp="ow1i2063lyxp">
            <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
            color: RED
          }} data-id="element-3606" data-mp="12w2dv17iag6b">
              {c.eyebrow}
            </p>
            <p className="font-nunito italic text-2xl sm:text-3xl mb-1" style={{
            color: NAVY
          }} data-id="element-3607" data-mp="ujpmsnys0ezk">
              {c.script}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.06em] leading-[0.95]" style={{
            color: NAVY
          }} data-id="element-3608" data-mp="zg6nehn5wltj">
              {c.title}
            </h1>
            <div className="flex items-center justify-center gap-2 mt-5 mb-5" data-id="element-3609" data-mp="bsyjo45dtktm">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-3.5 h-3.5" style={{
              color: NAVY
            }} fill={NAVY} data-id="element-3610" data-mp="gjx0r5y72mxx" />)}
            </div>
            <p className="text-base sm:text-lg leading-relaxed" style={{
            color: `${NAVY}cc`
          }} data-id="element-3611" data-mp="6uwwb70xazcc">
              {c.subtitle}
            </p>
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-[#12336b]/15 bg-[#12336b]/15" data-id="element-3612" data-mp="3t36bybl1bop1">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-white px-5 py-5 flex items-start gap-3" data-id="element-3613" data-mp="3jdzerhaaosho" data-mpi={i}>
                  <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{
                color: RED
              }} data-id="element-3614" data-mp="3ed1vt2hgnuf4" />
                  <div data-id="element-3615" data-mp="3od2jf0md4wp4">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" style={{
                  color: `${NAVY}b3`
                }} data-id="element-3616" data-mp="3obhm538nyznk">
                      {item.label}
                    </dt>
                    <dd className="text-sm font-semibold leading-snug" style={{
                  color: NAVY
                }} data-id="element-3617" data-mp="1zc73rlejiug">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Flyer + RSVP */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-3618" data-mp="38s0yvgxm7p96">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-3619" data-mp="3dp9htjql7xbh">
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.32, 1]
          }} className="flex" data-id="element-3620" data-mp="3ihkhqmlbvj41">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-xl ring-1 bg-white group" style={{
              boxShadow: '0 20px 50px -20px rgba(18,51,107,0.35)'
            }} aria-label={c.flyerAria} data-id="element-3621" data-mp="3sdvt9ad3g4xf">
                <img src={FLYER_IMAGE} alt={c.flyerAlt} className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-3622" data-mp="2712mgptgvmiz" />
              </a>
            </motion.aside>

            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: [0.16, 1, 0.32, 1]
          }} className="flex flex-col h-full" data-id="element-3623" data-mp="1ps9yw4870i5e">
              <div className="rounded-2xl border border-[#12336b]/15 bg-white overflow-hidden flex flex-col flex-1 shadow-sm" data-id="element-3624" data-mp="2igfksuazgd6t">
                <div className="px-6 pt-6 pb-4" data-id="element-3625" data-mp="28j1a7bbqqimf">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1 rounded-full text-white mb-3" style={{
                  backgroundColor: RED
                }} data-id="element-3626" data-mp="28hq77dq4dewd">
                    {c.rsvpBadge}
                  </span>
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-2" style={{
                  color: NAVY
                }} data-id="element-3627" data-mp="t1hjz9wzww57">
                    <MailCheckIcon className="w-5 h-5" style={{
                    color: RED
                  }} data-id="element-3628" data-mp="2lqqngi10v2zu" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="text-sm leading-relaxed" style={{
                  color: `${NAVY}b3`
                }} data-id="element-3629" data-mp="272lzozn441a7">
                    {c.rsvpText}
                  </p>
                </div>

                <div className="flex-1 bg-[#e6e1d5] border-y border-[#0b2148]/20" data-id="element-3630" data-mp="34ojdijqjrift">
                  {RSVP_EMBED_URL ? <iframe src={RSVP_EMBED_URL} title={c.rsvpFrameTitle} className="w-full h-full min-h-[520px] block border-0 bg-white" allow="payment" data-id="element-3631" data-mp="2qpbg9wdy2ke9" /> : <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-center px-8 py-12 gap-5" data-id="element-3632" data-mp="1jayff0kcvogw">
                      <p className="text-sm leading-relaxed max-w-xs" style={{
                    color: `${NAVY}b3`
                  }} data-id="element-3633" data-mp="1bqe5dgdpay4y">
                        {c.rsvpPending}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3" data-id="element-3634" data-mp="1lniu9nyxl7pg">
                        <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-bold transition-colors hover:opacity-90" style={{
                      backgroundColor: NAVY
                    }} data-id="element-3635" data-mp="1grbkfk3ezc28">
                          <PhoneIcon className="w-4 h-4" data-id="element-3636" data-mp="y0kgiy274dv8" />
                          {CONTACT_PHONE}
                        </a>
                        <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-bold transition-colors hover:opacity-90" style={{
                      backgroundColor: RED
                    }} data-id="element-3637" data-mp="a9ogxeri0edf">
                          <MailIcon className="w-4 h-4" data-id="element-3638" data-mp="yzjcqjfszjvv" />
                          {isEs ? 'Enviar correo' : 'Email us'}
                        </a>
                      </div>
                    </div>}
                </div>

                <div className="px-6 py-4" data-id="element-3639" data-mp="c16dugwcecdx">
                  <p className="text-sm leading-relaxed" style={{
                  color: `${NAVY}99`
                }} data-id="element-3640" data-mp="c2hfob689a2e">
                    {c.contactLead}{' '}
                    <a href={`tel:${CONTACT_PHONE.replace(/-/g, '')}`} className="font-semibold hover:underline" style={{
                    color: RED
                  }} data-id="element-3641" data-mp="g4bd8al4lxyc">
                      {CONTACT_PHONE}
                    </a>
                    {' · '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold hover:underline" style={{
                    color: RED
                  }} data-id="element-3642" data-mp="21fun499mze7q">
                      {CONTACT_EMAIL}
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About */}
        <section className="border-t border-[#12336b]/10" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-3643" data-mp="379942lfralgq">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-3644" data-mp="338fgjt6878od">
            <div className="text-center max-w-3xl mx-auto" data-id="element-3645" data-mp="2y81eqsv7wuys">
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4" style={{
              color: RED
            }} data-id="element-3646" data-mp="2yankqo2gn2ew">
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" style={{
              color: NAVY
            }} data-id="element-3647" data-mp="2ochiu6gnlwa1">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px mx-auto mb-8" style={{
              backgroundColor: RED
            }} data-id="element-3648" data-mp="3rj203jix9d28" />
            </div>
            <div className="space-y-5 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" style={{
            color: `${NAVY}cc`
          }} data-id="element-3649" data-mp="bpk37trz5nw">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-3650" data-mp="38sanm93n16jm">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-3651" data-mp="38qzledpuc6um">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.1,
              ease: [0.16, 1, 0.32, 1]
            }} className="rounded-2xl border border-[#0b2148]/20 bg-[#e6e1d5] p-7 text-center" data-id="element-3652" data-mp="3dp11qqvvts2e" data-mpi={i}>
                  <div className="w-10 h-px mb-5 mx-auto" style={{
                backgroundColor: RED
              }} data-id="element-3653" data-mp="2iia9xsq047n1" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" style={{
                color: NAVY
              }} data-id="element-3654" data-mp="2wagkmzadxmf3">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{
                color: `${NAVY}b3`
              }} data-id="element-3655" data-mp="3661kaqrzealo">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3656" data-mp="3a7n4kx6uhr5w">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center text-white" data-id="element-3657" data-mp="1kvl0wywvsuro">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-3658" data-mp="1fw88zzr5kxi8">
              {c.missionHeading}
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-white/80 mb-5" data-id="element-3659" data-mp="o4v9i19bb0xs">
              {c.missionText}
            </p>
            <p className="font-bebas text-3xl tracking-wider" data-id="element-3660" data-mp="wz1mf5vciojy">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="py-8 text-center bg-white border-t border-[#12336b]/10" data-id="element-3661" data-mp="syhpa2arvkxt">
        <p className="text-xs uppercase tracking-[0.2em]" style={{
        color: `${NAVY}b3`
      }} data-id="element-3662" data-mp="58wxctwn5dce">
          {c.footer}
        </p>
      </footer>
    </div>;
}